import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, message, Form } from 'antd';
import { ArrowRightOutlined, BankOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import BankAccountForm from '../../components/finance/BankAccountForm';
import { FormSkeleton } from '../../components/SkeletonLoaders';
import { bankAccountsApi, show422Error, ValidationError } from '../../utils/api';
import { getDefaultCurrencyId } from '../../utils/currencyUtils';

const AddBankAccount = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [accountType, setAccountType] = useState('bank');
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => {
    fetchCreateData();
  }, []);

  const fetchCreateData = async () => {
    setLoading(true);
    try {
      const res = await bankAccountsApi.getCreateData();
      if (res?.data?.currencies) {
        setCurrencies(res.data.currencies);
        if (res.data.currencies.length > 0) {
          form.setFieldValue('currency_id', getDefaultCurrencyId(res.data.currencies));
        }
      }
    } catch (error) {
      console.error('Error fetching create data:', error);
      message.error('خطا در دریافت اطلاعات اولیه');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (e) => {
    setAccountType(e.target.value);
    if (e.target.value === 'cash') {
      form.setFieldsValue({
        bank_name: undefined,
        account_number: undefined,
        account_type: undefined,
      });
      setLogoFile(null);
    }
  };

  const handleLogoUpload = (info) => {
    if (info.file) {
      setLogoFile(info.file);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        type: values.type,
        account_name: values.account_name,
        currency_id: values.currency_id,
        contact_number: values.contact_number || '',
        opening_balance: values.opening_balance || 0,
        status: values.status ? 1 : 0,
      };

      if (values.type === 'bank') {
        payload.bank_name = values.bank_name;
        payload.account_number = values.account_number;
        payload.account_type = values.account_type;
        if (logoFile) {
          payload.bank_logo = logoFile;
        }
      }

      await bankAccountsApi.create(payload);
      message.success('حساب بانکی با موفقیت ایجاد شد');
      navigate('/finance/bank-accounts');
    } catch (error) {
      console.error('Error creating bank account:', error);
      if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ایجاد حساب بانکی');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="" style={{ fontFamily: 'CustomFont' }}>
          <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
            <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <BankOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">افزودن حساب بانکی جدید</h1>
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
              <FormSkeleton />
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <BankOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  افزودن حساب بانکی جدید
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0 mt-1">
                  اطلاعات حساب بانکی یا صندوق نقدی را تکمیل کنید
                </p>
              </div>
            </div>
            <Button
              icon={<ArrowRightOutlined />}
              onClick={() => navigate('/finance/bank-accounts')}
            >
              بازگشت
            </Button>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="dark:bg-gray-800 border-0 shadow-sm">
              <BankAccountForm
                form={form}
                accountType={accountType}
                onAccountTypeChange={handleTypeChange}
                currencies={currencies}
                submitting={submitting}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/finance/bank-accounts')}
                logoFile={logoFile}
                onLogoUpload={handleLogoUpload}
              />
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddBankAccount;
